import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Calculator, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';

const API_URL = getApiBaseUrl();

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickBodyMeasurement = (snapshot, keys = []) => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  for (const key of keys) {
    const value = parseNumber(snapshot[key]);
    if (value !== null) return value;
  }
  return null;
};

const toBoolFromCustomization = (customizations, matcher) => {
  const match = (customizations || []).find(matcher);
  return !!match;
};

const buildFallbackFromCustomerMeasurement = (garmentType, measurement, fabricType = 'main_fabric') => {
  const type = (garmentType || '').toLowerCase();

  if (type.includes('trouser')) {
    return {
      patternMeasurements: {
        waist: parseNumber(measurement.lower_waist_circumference || measurement.waist_circumference),
        thigh: parseNumber(measurement.thigh_circumference),
        outseam: parseNumber(measurement.outseam),
        pattern_code: 'Body Measurements',
        garment_type: garmentType,
        fabric_type: fabricType,
      },
      bodyInputs: {
        formula_version: 'v7.17',
        body_thigh: parseNumber(measurement.thigh_circumference),
        outseam: parseNumber(measurement.outseam),
      },
    };
  }

  if (type.includes('shirt')) {
    return {
      patternMeasurements: {
        chest_circumference: parseNumber(measurement.chest_circumference),
        belly_circumference: parseNumber(measurement.belly_circumference),
        sleeve_length: parseNumber(measurement.sleeve_length),
        shirt_length: parseNumber(measurement.front_length_upper),
        pattern_code: 'Body Measurements',
        garment_type: garmentType,
        fabric_type: fabricType,
      },
      bodyInputs: {
        formula_version: 'v7.17',
        body_chest: parseNumber(measurement.chest_circumference),
        body_belly: parseNumber(measurement.belly_circumference),
        body_biceps: parseNumber(measurement.biceps_circumference),
        shirt_length: parseNumber(measurement.front_length_upper),
        sleeve_length: parseNumber(measurement.sleeve_length),
      },
    };
  }

  if (type.includes('waistcoat')) {
    return {
      patternMeasurements: {
        chest_circumference: parseNumber(measurement.chest_circumference),
        waistcoat_length: parseNumber(measurement.waistcoat_front_length),
        pattern_code: 'Body Measurements',
        garment_type: garmentType,
        fabric_type: fabricType,
      },
      bodyInputs: {
        formula_version: 'v7.17',
        body_chest: parseNumber(measurement.chest_circumference),
        body_belly: parseNumber(measurement.belly_circumference),
        length: parseNumber(measurement.waistcoat_front_length),
      },
    };
  }

  // Default jacket/overcoat
  return {
    patternMeasurements: {
      chest_circumference: parseNumber(measurement.chest_circumference),
      belly_circumference: parseNumber(measurement.belly_circumference),
      sleeve_length: parseNumber(measurement.sleeve_length),
      front_length_upper: parseNumber(measurement.front_length_upper),
      pattern_code: 'Body Measurements',
      garment_type: garmentType,
      fabric_type: fabricType,
    },
    bodyInputs: {
      formula_version: 'v7.17',
      body_chest: parseNumber(measurement.chest_circumference),
      body_belly: parseNumber(measurement.belly_circumference),
      body_biceps: parseNumber(measurement.biceps_circumference),
      jacket_len: parseNumber(measurement.front_length_upper),
      sleeve_len: parseNumber(measurement.sleeve_length),
    },
  };
};

const getMeasurementSourceLabel = (source) => {
  if (source === 'pattern') return 'Using Pattern Measurements';
  if (source === 'body') return 'Using Body Measurements';
  return 'Measurement Source Pending';
};

const getPatternRequirements = (garmentType) => {
  const type = (garmentType || '').toLowerCase();
  if (type.includes('trouser')) {
    return [
      { key: 'waist', label: 'Waist' },
      { key: 'thigh', label: 'Thigh' },
      { key: 'outseam', label: 'Outseam Length' },
    ];
  }
  if (type.includes('shirt')) {
    return [
      { key: 'chest_circumference', label: 'Chest' },
      { key: 'belly_circumference', label: 'Belly' },
      { key: 'sleeve_length', label: 'Sleeve Length' },
      { key: 'shirt_length', label: 'Shirt Length' },
    ];
  }
  if (type.includes('waistcoat')) {
    return [
      { key: 'chest_circumference', label: 'Chest' },
      { key: 'waistcoat_length', label: 'Waistcoat Length' },
    ];
  }
  return [
    { key: 'chest_circumference', label: 'Chest' },
    { key: 'belly_circumference', label: 'Belly' },
    { key: 'sleeve_length', label: 'Sleeve Length' },
    { key: 'front_length_upper', label: 'Front Length' },
  ];
};

const getLogicExplanation = (garmentType, source) => {
  const type = (garmentType || '').toLowerCase();
  if (type.includes('trouser') && source === 'body') {
    return [
      'Finished thigh = body thigh + ease',
      'Base length = outseam + 1.5" + inlay 2.0" + seam 0.5"',
      'Dynamic offset: 4" up to thigh 26, then +1" per extra 0.5"',
      'Checked fabric multiplier (+10%) if enabled',
      'Add 0.05m buffer and round up to nearest 0.05m',
    ];
  }
  if (type.includes('trouser') && source === 'pattern') {
    return [
      'Width zones from thigh + waist-fit check choose layout strategy',
      'Leg height = outseam + 2.5"',
      'If stacked: 2 leg lengths, else 1 length + strategy offset',
      'Checked fabric multiplier (+10%) if enabled',
      'Add reserve buffer and round as per legacy rule',
    ];
  }
  if (source === 'pattern') {
    return [
      'Pattern measurements are used directly',
      'Design details from line-item customizations are auto-applied',
      'Fabric type flags (checked/striped/DB/wool-back) modify quantity by garment logic',
    ];
  }
  return [
    'Latest customer body measurements are used',
    'Ease and design details are inferred from line-item customization',
    'Formula v7.17 body-measurement logic is applied',
    'Result is previewed and saved only on Confirm & Apply',
  ];
};



const FabricCalculatorModal = ({ open, onOpenChange, fabricOrder, onConfirm }) => {
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [measurements, setMeasurements] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [isDoubleBreasted, setIsDoubleBreasted] = useState(false);
  const [isCheckedFabric, setIsCheckedFabric] = useState(false);
  const [isStripedFabric, setIsStripedFabric] = useState(false);
  const [pleats, setPleats] = useState(0);
  const [hasWoolBack, setHasWoolBack] = useState(false);
  const [calculatedQty, setCalculatedQty] = useState(null);
  const [manualOverride, setManualOverride] = useState('');
  const [calculationResult, setCalculationResult] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [bodyInputs, setBodyInputs] = useState(null);
  const [measurementSource, setMeasurementSource] = useState(null);

  // Fetch customer measurements and detect DB from customizations
  useEffect(() => {
    if (!open || !fabricOrder) return;

    const fetchData = async () => {
      setLoading(true);
      setBodyInputs(null);
      setErrorMessage(null);
      setMeasurementSource(null);
      try {
        const token = localStorage.getItem('token');

        // Store customer info for display
        setCustomerInfo({
          customer_name: fabricOrder.customer_name,
          customer_no: fabricOrder.customer_no,
          customer_id: fabricOrder.customer_id,
        });

        // 1. Resolve line-item design customizations (fallback to order line item if needed)
        let customizations = fabricOrder.customizations || [];
        let resolvedGarmentType = fabricOrder.garment_type || '';

        if ((!customizations || customizations.length === 0) && fabricOrder.order_id && fabricOrder.line_item_id) {
          try {
            const orderRes = await fetch(`${API_URL}/api/orders/${fabricOrder.order_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (orderRes.ok) {
              const orderData = await orderRes.json();
              const lineItem = (orderData.line_items || []).find(
                (li) => li.line_item_id === fabricOrder.line_item_id
              );
              if (lineItem) {
                customizations = lineItem.customizations || [];
                resolvedGarmentType = lineItem.garment_type || resolvedGarmentType;
              }
            }
          } catch (lineItemError) {
            console.warn('Unable to resolve line-item customizations fallback:', lineItemError);
          }
        }

        // 1b. Detect style controls from customizations
        
        const dbCustomization = customizations.find(c => {
          const optionName = c.option_name?.toLowerCase() || '';
          const categoryName = c.category_name?.toLowerCase() || '';
          const selectedValue = c.selected_value?.toLowerCase() || '';
          
          return (
            (optionName.includes('style') || 
             optionName.includes('jacket style') ||
             optionName.includes('front style') ||
             categoryName.includes('style')) &&
            selectedValue.includes('double')
          );
        });
        
        setIsDoubleBreasted(!!dbCustomization);

        const shawlCustomization = toBoolFromCustomization(customizations, (c) => {
          const optionName = c.option_name?.toLowerCase() || '';
          const categoryName = c.category_name?.toLowerCase() || '';
          const selectedValue = c.selected_value?.toLowerCase() || '';
          return (
            (optionName.includes('lapel') || categoryName.includes('lapel')) &&
            selectedValue.includes('shawl')
          );
        });

        const comfortEaseCustomization = toBoolFromCustomization(customizations, (c) => {
          const optionName = c.option_name?.toLowerCase() || '';
          const categoryName = c.category_name?.toLowerCase() || '';
          const selectedValue = c.selected_value?.toLowerCase() || '';
          return (
            optionName.includes('ease') ||
            categoryName.includes('ease') ||
            selectedValue.includes('comfort')
          );
        });
        const isShirtGarment = (resolvedGarmentType || fabricOrder.garment_type || '').toLowerCase().includes('shirt');
        const resolvedEase = comfortEaseCustomization ? 5.0 : (isShirtGarment ? 4.0 : 3.5);

        // 1b. Detect Pleats for Trouser (from customizations)
        const pleatCustomization = customizations.find(c => {
          const optionName = c.option_name?.toLowerCase() || '';
          const categoryName = c.category_name?.toLowerCase() || '';
          return (
            optionName.includes('pleat') || 
            categoryName.includes('pleat')
          );
        });

        if (pleatCustomization) {
          const pleatValue = pleatCustomization.selected_value?.toLowerCase() || '';
          let pleatCount = 0;
          
          if (pleatValue.includes('double')) {
            pleatCount = 2;
          } else if (pleatValue.includes('single')) {
            pleatCount = 1;
          } else {
            pleatCount = 0; // Flat Front
          }
          
          setPleats(pleatCount);
        } else {
          setPleats(0);
        }

        // 1c. Detect Wool Back for Waistcoat (from customizations - Back Details category)
        const backFabricCustomization = customizations.find(c => {
          const optionName = c.option_name?.toLowerCase() || '';
          const categoryName = c.category_name?.toLowerCase() || '';
          return (
            categoryName.includes('back detail') && 
            optionName.includes('back fabric')
          );
        });

        let detectedHasWoolBack = false;
        if (backFabricCustomization) {
          const backValue = backFabricCustomization.selected_value?.toLowerCase() || '';
          // If "Main Fabric" or similar, set wool back to true
          detectedHasWoolBack = backValue.includes('main') || backValue.includes('same');
          setHasWoolBack(detectedHasWoolBack);
        } else {
          // Default to lining back if not specified
          detectedHasWoolBack = false;
          setHasWoolBack(false);
        }

        // 2. Resolve garment + optional pattern_no
        let garmentData = null;
        let patternNo = null;
        if (fabricOrder.garment_id) {
          const garmentRes = await fetch(
            `${API_URL}/api/garments/${fabricOrder.garment_id}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (garmentRes.ok) {
            garmentData = await garmentRes.json();
            resolvedGarmentType = garmentData.garment_type || resolvedGarmentType;
            patternNo = garmentData.pattern_no?.trim();
          }
        }

        console.log('Resolved Pattern No:', patternNo || '<none>');

        // 3. Fetch pattern measurements using pattern_code
        // 3A. Pattern-driven path (if pattern is available)
        if (patternNo) {
          const patternRes = await fetch(
            `${API_URL}/api/patterns?pattern_code=${encodeURIComponent(patternNo)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (!patternRes.ok) {
            throw new Error('Failed to fetch pattern');
          }

          const patternsData = await patternRes.json();
          console.log('Patterns fetched:', patternsData.length);
          
          if (!patternsData || patternsData.length === 0) {
            console.log('Pattern not found:', patternNo);
            setMeasurements(null);
            setErrorMessage(`Pattern '${patternNo}' not found in database`);
            return;
          }

          const pattern = patternsData[0];
          const specs = pattern.garment_specifications || {};
          const bodySnapshot = pattern.body_measurement_snapshot || {};
        
        console.log('Pattern garment_specifications:', specs);

        // Map pattern fields based on garment type
        let patternMeasurements;
        
        let resolvedBodyInputs = null;

          if ((resolvedGarmentType || '').toLowerCase() === 'trouser') {
          // Trouser measurements
          patternMeasurements = {
            waist: specs.tr_waist ? parseFloat(specs.tr_waist) : null,
            thigh: specs.tr_thigh ? parseFloat(specs.tr_thigh) : null,
            outseam: specs.tr_outseam_length ? parseFloat(specs.tr_outseam_length) : null,
            pattern_code: patternNo,
            garment_type: resolvedGarmentType,
            fabric_type: fabricOrder.fabric_type,
          };

          resolvedBodyInputs = {
            formula_version: 'v7.17',
            body_thigh: pickBodyMeasurement(bodySnapshot, ['tr_thigh', 'thigh']),
            outseam: parseNumber(specs.tr_outseam_length),
            ease: resolvedEase,
            is_checked_fabric: false,
          };
          } else if ((resolvedGarmentType || '').toLowerCase() === 'shirt') {
          // Shirt measurements
          patternMeasurements = {
            chest_circumference: specs.sh_chest || specs.jk_chest ? parseFloat(specs.sh_chest || specs.jk_chest) : null,
            belly_circumference: specs.sh_belly || specs.jk_belly ? parseFloat(specs.sh_belly || specs.jk_belly) : null,
            sleeve_length: specs.sh_sleeve_length || specs.jk_full_sleeve_length ? parseFloat(specs.sh_sleeve_length || specs.jk_full_sleeve_length) : null,
            shirt_length: specs.sh_front_length || specs.jk_jacket_length_front ? parseFloat(specs.sh_front_length || specs.jk_jacket_length_front) : null,
            pattern_code: patternNo,
            garment_type: resolvedGarmentType,
            fabric_type: fabricOrder.fabric_type,
          };

          resolvedBodyInputs = {
            formula_version: 'v7.17',
            body_chest: pickBodyMeasurement(bodySnapshot, ['sh_chest', 'jk_chest', 'chest']),
            body_belly: pickBodyMeasurement(bodySnapshot, ['sh_belly', 'jk_belly', 'belly']),
            body_biceps: pickBodyMeasurement(bodySnapshot, ['sh_biceps', 'jk_biceps', 'biceps']),
            shirt_length: parseNumber(specs.sh_front_length || specs.jk_jacket_length_front),
            sleeve_length: parseNumber(specs.sh_sleeve_length || specs.jk_full_sleeve_length),
            ease: resolvedEase,
          };
          } else if ((resolvedGarmentType || '').toLowerCase() === 'waistcoat') {
          // Waistcoat measurements - Use Front Length as waistcoat length
          console.log('Waistcoat specs.wc_front_length:', specs.wc_front_length);
          console.log('Waistcoat specs.wc_chest:', specs.wc_chest);
          patternMeasurements = {
            chest_circumference: specs.wc_chest ? parseFloat(specs.wc_chest) : null,
            waistcoat_length: specs.wc_front_length ? parseFloat(specs.wc_front_length) : null,
            pattern_code: patternNo,
            garment_type: resolvedGarmentType,
            fabric_type: fabricOrder.fabric_type,
          };

          resolvedBodyInputs = {
            formula_version: 'v7.17',
            body_chest: pickBodyMeasurement(bodySnapshot, ['wc_chest', 'jk_chest', 'chest']),
            body_belly: pickBodyMeasurement(bodySnapshot, ['wc_belly', 'jk_belly', 'belly']),
            length: parseNumber(specs.wc_front_length),
            ease: resolvedEase,
            has_wool_back: detectedHasWoolBack,
            is_double_breasted: !!dbCustomization,
          };
          console.log('Waistcoat patternMeasurements:', patternMeasurements);
        } else {
          // Jacket/Overcoat measurements
          patternMeasurements = {
            chest_circumference: specs.jk_chest ? parseFloat(specs.jk_chest) : null,
            belly_circumference: specs.jk_belly ? parseFloat(specs.jk_belly) : null,
            sleeve_length: specs.jk_full_sleeve_length ? parseFloat(specs.jk_full_sleeve_length) : null,
            front_length_upper: specs.jk_jacket_length_front ? parseFloat(specs.jk_jacket_length_front) : null,
            pattern_code: patternNo,
            garment_type: resolvedGarmentType,
            fabric_type: fabricOrder.fabric_type,
          };

          resolvedBodyInputs = {
            formula_version: 'v7.17',
            body_chest: pickBodyMeasurement(bodySnapshot, ['jk_chest', 'chest']),
            body_belly: pickBodyMeasurement(bodySnapshot, ['jk_belly', 'belly']),
            body_biceps: pickBodyMeasurement(bodySnapshot, ['jk_biceps', 'biceps']),
            jacket_len: parseNumber(specs.jk_jacket_length_front),
            sleeve_len: parseNumber(specs.jk_full_sleeve_length),
            ease: resolvedEase,
            lapel_type: shawlCustomization ? 'shawl' : 'normal',
            is_double_breasted: !!dbCustomization,
          };
        }

          // Pattern mode: do not use body fallback when pattern is present
          const requiredFields = getPatternRequirements(resolvedGarmentType);
          const missingPatternFields = requiredFields
            .filter((f) => {
              const value = patternMeasurements[f.key];
              return value === null || value === undefined || value === '';
            })
            .map((f) => f.label);

          if (missingPatternFields.length > 0) {
            setMeasurements(null);
            setBodyInputs(null);
            setMeasurementSource('pattern');
            setErrorMessage(
              `Pattern '${patternNo}' is missing required fields: ${missingPatternFields.join(', ')}`
            );
            return;
          }

          setBodyInputs(null);
          setMeasurementSource('pattern');
          console.log('Mapped pattern measurements:', patternMeasurements);
          setMeasurements(patternMeasurements);
          return;
        }

        // 3B. Body-measurement fallback path (no pattern required)
        if (!fabricOrder.customer_id) {
          setMeasurements(null);
          setErrorMessage('No customer linked for body measurement fallback');
          return;
        }

        const measurementRes = await fetch(
          `${API_URL}/api/measurements?customer_id=${encodeURIComponent(fabricOrder.customer_id)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!measurementRes.ok) {
          throw new Error('Failed to fetch customer body measurements');
        }
        const measurementList = await measurementRes.json();
        if (!measurementList || measurementList.length === 0) {
          setMeasurements(null);
          setErrorMessage('No body measurements found for this customer');
          return;
        }

        const latestMeasurement = [...measurementList].sort((a, b) => {
          const dateA = new Date(a.measurement_date || a.created_at || 0).getTime();
          const dateB = new Date(b.measurement_date || b.created_at || 0).getTime();
          return dateB - dateA;
        })[0];
        const fallback = buildFallbackFromCustomerMeasurement(
          resolvedGarmentType || fabricOrder.garment_type || 'jacket',
          latestMeasurement,
          fabricOrder.fabric_type
        );

        // Attach resolved ease + design toggles to body inputs
        const mergedBodyInputs = {
          ...fallback.bodyInputs,
          ease: resolvedEase,
          is_double_breasted: !!dbCustomization,
          has_wool_back: detectedHasWoolBack,
          lapel_type: shawlCustomization ? 'shawl' : 'normal',
          is_checked_fabric: false,
        };

        setBodyInputs(mergedBodyInputs);
        setMeasurementSource('body');
        setMeasurements(fallback.patternMeasurements);
        setErrorMessage(null);

      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load measurement data');
        setMeasurements(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, fabricOrder]);

  // Auto-calculate when measurements are loaded
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (measurements && !calculating) {
      handleCalculate();
    }
  }, [measurements, isDoubleBreasted, isCheckedFabric, isStripedFabric, pleats, hasWoolBack]);

  const handleCalculate = async () => {
    if (!measurements) return;

    const garmentType = measurements.garment_type?.toLowerCase() || 'jacket';
    let requestBody = {
      garment_type: garmentType,
      fabric_type: measurements.fabric_type || 'main_fabric',
      is_checked_fabric: isCheckedFabric,
      formula_version: measurementSource === 'body' ? 'v7.17' : 'legacy',
    };

    if (measurementSource === 'body' && bodyInputs) {
      requestBody = {
        ...requestBody,
        ...bodyInputs,
        is_checked_fabric: isCheckedFabric,
      };
    }

    // Build request based on garment type
    if (garmentType === 'trouser') {
      // Trouser measurements
      const waist = measurements.waist;
      const thigh = measurements.thigh;
      const outseam = measurements.outseam;

      const hasBodyV717Inputs =
        measurementSource === 'body' &&
        bodyInputs &&
        bodyInputs.body_thigh !== null &&
        bodyInputs.body_thigh !== undefined &&
        bodyInputs.outseam !== null &&
        bodyInputs.outseam !== undefined &&
        bodyInputs.ease !== null &&
        bodyInputs.ease !== undefined;

      // Validate trouser measurements only for legacy fallback
      if (!hasBodyV717Inputs && (!waist || !thigh || !outseam)) {
        const missing = [];
        if (!waist) missing.push('waist');
        if (!thigh) missing.push('thigh');
        if (!outseam) missing.push('outseam length');
        
        toast.error(`Missing trouser measurements: ${missing.join(', ')}`);
        return;
      }

      requestBody = {
        ...requestBody,
        waist: waist ? parseFloat(waist) : null,
        thigh: thigh ? parseFloat(thigh) : null,
        outseam: outseam ? parseFloat(outseam) : null,
        pleats: parseInt(pleats),
      };
    } else if (garmentType === 'shirt') {
      // Shirt measurements
      const chest = measurements.chest_circumference || measurements.chest;
      const belly = measurements.belly_circumference || measurements.belly;
      const sleeve_length = measurements.sleeve_length;
      const shirt_length = measurements.shirt_length;

      const hasBodyV717Inputs =
        measurementSource === 'body' &&
        bodyInputs &&
        bodyInputs.body_chest !== null &&
        bodyInputs.body_chest !== undefined &&
        bodyInputs.body_belly !== null &&
        bodyInputs.body_belly !== undefined &&
        bodyInputs.body_biceps !== null &&
        bodyInputs.body_biceps !== undefined &&
        bodyInputs.shirt_length !== null &&
        bodyInputs.shirt_length !== undefined &&
        bodyInputs.sleeve_length !== null &&
        bodyInputs.sleeve_length !== undefined &&
        bodyInputs.ease !== null &&
        bodyInputs.ease !== undefined;

      // Validate shirt measurements only for legacy fallback
      if (!hasBodyV717Inputs && (!chest || !belly || !sleeve_length || !shirt_length)) {
        const missing = [];
        if (!chest) missing.push('chest circumference');
        if (!belly) missing.push('belly circumference');
        if (!sleeve_length) missing.push('sleeve length');
        if (!shirt_length) missing.push('shirt length');
        
        toast.error(`Missing shirt measurements: ${missing.join(', ')}`);
        return;
      }

      requestBody = {
        ...requestBody,
        chest: chest ? parseFloat(chest) : null,
        belly: belly ? parseFloat(belly) : null,
        sleeve_length: sleeve_length ? parseFloat(sleeve_length) : null,
        shirt_length: shirt_length ? parseFloat(shirt_length) : null,
        is_striped_fabric: isStripedFabric,
      };
    } else if (garmentType === 'waistcoat') {
      // Waistcoat measurements
      const chest = measurements.chest_circumference || measurements.chest;
      const length = measurements.waistcoat_length;

      console.log('Waistcoat calculate - chest:', chest);
      console.log('Waistcoat calculate - length:', length);
      console.log('Waistcoat calculate - measurements:', measurements);

      const hasBodyV717Inputs =
        measurementSource === 'body' &&
        bodyInputs &&
        bodyInputs.body_chest !== null &&
        bodyInputs.body_chest !== undefined &&
        bodyInputs.body_belly !== null &&
        bodyInputs.body_belly !== undefined &&
        bodyInputs.length !== null &&
        bodyInputs.length !== undefined &&
        bodyInputs.ease !== null &&
        bodyInputs.ease !== undefined;

      // Validate waistcoat measurements only for legacy fallback
      if (!hasBodyV717Inputs && (!chest || !length)) {
        const missing = [];
        if (!chest) missing.push('chest circumference');
        if (!length) missing.push('waistcoat length');
        
        toast.error(`Missing waistcoat measurements: ${missing.join(', ')}`);
        return;
      }

      requestBody = {
        ...requestBody,
        chest: chest ? parseFloat(chest) : null,
        length: length ? parseFloat(length) : null,
        is_double_breasted: isDoubleBreasted,
        has_wool_back: hasWoolBack,
      };
      
      console.log('Waistcoat requestBody:', requestBody);
    } else {
      // Jacket/Overcoat measurements
      const chest = measurements.chest_circumference || measurements.chest;
      const belly = measurements.belly_circumference || measurements.belly;
      const sleeve_length = measurements.sleeve_length;
      const front_length = measurements.front_length_upper || measurements.front_length || measurements.jacket_length;

      const hasBodyV717Inputs =
        measurementSource === 'body' &&
        bodyInputs &&
        bodyInputs.body_chest !== null &&
        bodyInputs.body_chest !== undefined &&
        bodyInputs.body_belly !== null &&
        bodyInputs.body_belly !== undefined &&
        bodyInputs.body_biceps !== null &&
        bodyInputs.body_biceps !== undefined &&
        bodyInputs.jacket_len !== null &&
        bodyInputs.jacket_len !== undefined &&
        bodyInputs.sleeve_len !== null &&
        bodyInputs.sleeve_len !== undefined &&
        bodyInputs.ease !== null &&
        bodyInputs.ease !== undefined;

      // Validate jacket measurements only for legacy fallback
      if (!hasBodyV717Inputs && (!chest || !belly || !sleeve_length || !front_length)) {
        const missing = [];
        if (!chest) missing.push('chest circumference');
        if (!belly) missing.push('belly circumference');
        if (!sleeve_length) missing.push('sleeve length');
        if (!front_length) missing.push('front length');
        
        toast.error(`Missing measurements: ${missing.join(', ')}`);
        return;
      }

      requestBody = {
        ...requestBody,
        chest: chest ? parseFloat(chest) : null,
        belly: belly ? parseFloat(belly) : null,
        jacket_len: front_length ? parseFloat(front_length) : null,
        sleeve_len: sleeve_length ? parseFloat(sleeve_length) : null,
        is_double_breasted: isDoubleBreasted,
        is_striped_fabric: isStripedFabric,
      };
    }

    setCalculating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/fabrics/calculate-consumption`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Calculation failed');
      }

      const result = await response.json();
      setCalculationResult(result);
      setCalculatedQty(result.quantity_meters);
      setManualOverride(result.quantity_meters.toString());
      
      // Store debug info for display
      if (result.debug) {
        setDebugInfo(result.debug);
      }

    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate fabric quantity');
    } finally {
      setCalculating(false);
    }
  };

  const handleConfirm = useCallback(() => {
    if (onConfirm) onConfirm(manualOverride || calculatedQty);
    onOpenChange(false);
  }, [onConfirm, onOpenChange, manualOverride, calculatedQty]);

  const handleOpenFittingStudio = () => {
    if (fabricOrder?.customer_id) {
      window.open(`/fitting-studio/${fabricOrder.customer_id}`, '_blank');
    }
  };
  // Debug render
  // console.log('Render Measurements:', measurements);
  // console.log('Render DebugInfo:', debugInfo);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Smart Fabric Quantity Calculator
          </DialogTitle>
          <DialogDescription>
            <div className="space-y-1">
              {/* Dynamic fabric type display */}
              {measurements && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="default" className="font-semibold">
                    {measurements.fabric_type === 'lining' ? 'Lining Fabric' : 'Main Fabric'}
                  </Badge>
                  <span className="text-sm text-slate-600">
                    {measurements.garment_type?.toLowerCase().includes('trouser')
                      ? 'Trouser'
                      : measurements.garment_type?.toLowerCase().includes('shirt')
                      ? 'Shirt'
                      : measurements.garment_type?.toLowerCase().includes('waistcoat')
                      ? 'Waistcoat'
                      : measurements.garment_type === 'overcoat' 
                      ? 'Overcoat' 
                      : 'Jacket'}
                  </span>
                  <Badge variant="outline" className="font-medium" data-testid="fabric-calculator-measurement-source-badge">
                    {getMeasurementSourceLabel(measurementSource)}
                  </Badge>
                </div>
              )}
              {customerInfo && (
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="font-medium text-slate-700">
                    {customerInfo.customer_name}
                  </span>
                  {customerInfo.customer_no && (
                    <Badge variant="outline" className="font-mono text-xs">
                      {customerInfo.customer_no}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 px-1 space-y-6 min-h-0">{/* Scrollable content */}
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : !measurements ? (
            // No measurements found
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                <div className="space-y-3">
                  <p className="font-medium">Calculation not possible</p>
                  <p className="text-sm">
                    {errorMessage || 'Required body measurements or design details are missing.'}
                  </p>
                  <p className="text-sm text-slate-600">
                    Please ensure the customer has body measurements and this line item has design details.
                    Pattern number is now optional when those inputs are available.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenFittingStudio}
                    className="mt-2"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Go to Fitting Studio (New Tab)
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Input Parameters Section */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                  {measurementSource === 'pattern'
                    ? `Pattern Measurements (Auto-Filled from ${measurements.pattern_code})`
                    : 'Body Measurement Inputs (Latest Customer Measurement)'}
                </h3>

                {/* Measurements Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {measurements.garment_type?.toLowerCase().includes('trouser') ? (
                    /* Trouser Measurements */
                    <>
                      <div>
                        <Label className="text-xs text-slate-600">Waist</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.waist || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Thigh</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.thigh || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Outseam Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.outseam || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Pleats</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Badge variant="outline" className="h-8 px-3 flex items-center justify-center w-full">
                            {pleats === 2 ? 'Double' : pleats === 1 ? 'Single' : 'Flat Front'}
                          </Badge>
                        </div>
                      </div>
                    </>
                  ) : measurements.garment_type?.toLowerCase().includes('shirt') ? (
                    /* Shirt Measurements */
                    <>
                      <div>
                        <Label className="text-xs text-slate-600">Chest</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.chest_circumference || measurements.chest || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Belly</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.belly_circumference || measurements.belly || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Sleeve Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.sleeve_length || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Shirt Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.shirt_length || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                    </>
                  ) : measurements.garment_type?.toLowerCase().includes('waistcoat') ? (
                    /* Waistcoat Measurements */
                    <>
                      <div>
                        <Label className="text-xs text-slate-600">Chest</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.chest_circumference || measurements.chest || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Waistcoat Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.waistcoat_length || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Jacket/Overcoat Measurements */
                    <>
                      <div>
                        <Label className="text-xs text-slate-600">Chest</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.chest_circumference || measurements.chest || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Belly</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.belly_circumference || measurements.belly || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Sleeve Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.sleeve_length || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Front Length</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input
                            value={measurements.front_length_upper || measurements.front_length || measurements.jacket_length || '—'}
                            disabled
                            className="h-8 text-sm bg-white"
                          />
                          <span className="text-xs text-slate-500">in</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* User Adjustable Inputs */}
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  {measurements.garment_type?.toLowerCase().includes('trouser') ? (
                    /* Trouser-specific UI */
                    <>
                      {/* Pleat Info (Auto-detected, not editable) */}
                      <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">
                            Pleat Style:
                          </span>
                          <Badge variant="default" className="font-medium">
                            {pleats === 2 ? 'Double Pleat' : pleats === 1 ? 'Single Pleat' : 'Flat Front'}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500 italic">Auto-detected from design</span>
                      </div>

                      {/* Fabric Type Selection for Trouser */}
                      {measurements.fabric_type !== 'lining' && (
                        <div className="space-y-2 bg-slate-50 p-3 rounded-md">
                          <p className="text-xs font-semibold text-slate-600 uppercase">Fabric Type</p>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkedFabric"
                              checked={isCheckedFabric}
                              onCheckedChange={(checked) => setIsCheckedFabric(checked)}
                            />
                            <label
                              htmlFor="checkedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Checked/Plaid Fabric
                              <span className="text-xs text-slate-500 ml-2">(+10% for pattern matching)</span>
                            </label>
                          </div>
                          
                          {!isCheckedFabric && (
                            <p className="text-xs text-slate-500 italic">Solid fabric (no additional buffer)</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : measurements.garment_type?.toLowerCase().includes('shirt') ? (
                    /* Shirt-specific UI */
                    <>
                      {/* Fabric Type Selection for Shirt */}
                      {measurements.fabric_type !== 'lining' && (
                        <div className="space-y-2 bg-slate-50 p-3 rounded-md">
                          <p className="text-xs font-semibold text-slate-600 uppercase">Fabric Type</p>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkedFabric"
                              checked={isCheckedFabric}
                              onCheckedChange={(checked) => {
                                setIsCheckedFabric(checked);
                                if (checked) setIsStripedFabric(false);
                              }}
                            />
                            <label
                              htmlFor="checkedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Checked/Plaid Fabric
                              <span className="text-xs text-slate-500 ml-2">(+0.1m buffer)</span>
                            </label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="stripedFabric"
                              checked={isStripedFabric}
                              onCheckedChange={(checked) => {
                                setIsStripedFabric(checked);
                                if (checked) setIsCheckedFabric(false);
                              }}
                            />
                            <label
                              htmlFor="stripedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Striped Fabric
                              <span className="text-xs text-slate-500 ml-2">(+0.1m buffer)</span>
                            </label>
                          </div>
                          
                          {!isCheckedFabric && !isStripedFabric && (
                            <p className="text-xs text-slate-500 italic">Solid fabric (no additional buffer)</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : measurements.garment_type?.toLowerCase().includes('waistcoat') ? (
                    /* Waistcoat-specific UI */
                    <>
                      {/* Double Breasted Info (Auto-detected, not editable) */}
                      <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">
                            Waistcoat Style:
                          </span>
                          <Badge variant={isDoubleBreasted ? "default" : "outline"} className="font-medium">
                            {isDoubleBreasted ? 'Double Breasted' : 'Single Breasted'}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500 italic">Auto-detected from design</span>
                      </div>

                      {/* Back Fabric Info (Auto-detected, not editable) */}
                      <div className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">
                            Back Fabric:
                          </span>
                          <Badge variant={hasWoolBack ? "default" : "outline"} className="font-medium">
                            {hasWoolBack ? 'Main Fabric (Wool Back)' : 'Lining'}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500 italic">Auto-detected from design</span>
                      </div>

                      {/* Fabric Options for Waistcoat */}
                      {measurements.fabric_type !== 'lining' && (
                        <div className="space-y-3 bg-slate-50 p-3 rounded-md">
                          <p className="text-xs font-semibold text-slate-600 uppercase">Fabric Options</p>

                          {/* Checked Fabric Toggle */}
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkedFabric"
                              checked={isCheckedFabric}
                              onCheckedChange={(checked) => setIsCheckedFabric(checked)}
                            />
                            <label
                              htmlFor="checkedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Checked/Plaid Fabric
                              <span className="text-xs text-slate-500 ml-2">(+10% for pattern matching)</span>
                            </label>
                          </div>
                          
                          {!isCheckedFabric && !hasWoolBack && (
                            <p className="text-xs text-slate-500 italic">Standard waistcoat with lining back</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Jacket/Overcoat UI */
                    <>
                      {/* Double Breasted Info (Auto-detected, not editable) */}
                      <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">
                            Jacket Style:
                          </span>
                          <Badge variant={isDoubleBreasted ? "default" : "outline"} className="font-medium">
                            {isDoubleBreasted ? 'Double Breasted' : 'Single Breasted'}
                          </Badge>
                        </div>
                        <span className="text-xs text-slate-500 italic">Auto-detected from design</span>
                      </div>

                      {/* Fabric Type Selection (Only for Main Fabric) */}
                      {measurements.fabric_type !== 'lining' && (
                        <div className="space-y-2 bg-slate-50 p-3 rounded-md">
                          <p className="text-xs font-semibold text-slate-600 uppercase">Fabric Type</p>
                          
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="checkedFabric"
                              checked={isCheckedFabric}
                              onCheckedChange={(checked) => {
                                setIsCheckedFabric(checked);
                                if (checked) setIsStripedFabric(false);
                              }}
                            />
                            <label
                              htmlFor="checkedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Checked/Plaid Fabric
                              <span className="text-xs text-slate-500 ml-2">(+0.1m buffer)</span>
                            </label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="stripedFabric"
                              checked={isStripedFabric}
                              onCheckedChange={(checked) => {
                                setIsStripedFabric(checked);
                                if (checked) setIsCheckedFabric(false);
                              }}
                            />
                            <label
                              htmlFor="stripedFabric"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Striped Fabric
                              <span className="text-xs text-slate-500 ml-2">(+0.1m buffer)</span>
                            </label>
                          </div>
                          
                          {!isCheckedFabric && !isStripedFabric && (
                            <p className="text-xs text-slate-500 italic">Solid fabric (no additional buffer)</p>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Lining Fabric Info */}
                  {measurements.fabric_type === 'lining' && (
                    <div className="bg-purple-50 border border-purple-200 rounded p-3">
                      <p className="text-sm font-medium text-purple-800">Lining Fabric</p>
                      <p className="text-xs text-purple-600 mt-1">
                        Calculated as 85% of main fabric (solid lining, no pattern matching needed)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Calculation Result */}
              {calculating ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm text-slate-600">Calculating optimal layout...</span>
                </div>
              ) : calculationResult ? (
                <div className="space-y-4">
                  {/* Result Summary */}
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-slate-600 uppercase tracking-wide">
                          Recommended Quantity
                        </Label>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-4xl font-bold text-primary">
                            {calculationResult.quantity_meters}
                          </span>
                          <span className="text-lg text-slate-600">meters</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant="outline" 
                          className={`${
                            calculationResult.is_oversize 
                              ? 'bg-orange-50 text-orange-700 border-orange-200' 
                              : 'bg-green-50 text-green-700 border-green-200'
                          }`}
                        >
                          {calculationResult.layout_strategy}
                        </Badge>
                      </div>
                    </div>

                    {calculationResult.is_oversize && (
                      <Alert className="mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="ml-2 text-sm">
                          <strong>Oversize Layout:</strong> This garment requires additional fabric due to 
                          larger body measurements or fabric pattern constraints.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Current Logic Explanation */}
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                      <span className="w-1 h-4 bg-primary rounded"></span>
                      Current Calculation Logic
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-700 list-disc pl-5" data-testid="fabric-calculator-logic-explanation-list">
                      {getLogicExplanation(measurements.garment_type, measurementSource).map((line, idx) => (
                        <li key={`${line}-${idx}`}>{line}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Simplified Technical Breakdown */}
                  {debugInfo && measurements.fabric_type !== 'lining' && (
                    <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                        <span className="w-1 h-4 bg-primary rounded"></span>
                        Technical Breakdown
                      </h4>

                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">Layout Strategy:</span>
                          <Badge variant="default" className="font-semibold">
                            {calculationResult.layout_strategy}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          Source: {measurementSource === 'pattern' ? 'Pattern measurements + line-item design' : 'Body measurements + line-item design'}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {Object.entries(debugInfo)
                          .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
                          .slice(0, 18)
                          .map(([key, value]) => (
                            <div key={key} className="flex justify-between gap-3 border-b border-slate-200 py-1">
                              <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="font-medium text-slate-900 text-right">{String(value)}</span>
                            </div>
                          ))}
                      </div>

                      <div className="border-t border-slate-200 pt-3 space-y-2 text-sm bg-primary/5 rounded p-3">
                        <div className="flex justify-between text-base">
                          <span className="text-slate-800 font-semibold">Final Quantity:</span>
                          <span className="font-bold text-primary">{calculationResult.quantity_meters}m</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Manual Override */}
                  <div className="border-t border-slate-200 pt-4">
                    <Label className="text-xs text-slate-700 font-medium">
                      Manual Override (Optional)
                    </Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Input
                        type="number"
                        step="0.1"
                        value={manualOverride}
                        onChange={(e) => setManualOverride(e.target.value)}
                        className="h-10 text-base font-medium"
                      />
                      <span className="text-sm text-slate-600">meters</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Adjust the calculated value if needed based on your expertise
                    </p>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>{/* End scrollable content */}

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {measurements && calculationResult && (
            <Button onClick={handleConfirm} data-testid="fabric-calculator-confirm-apply-button">
              Confirm & Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FabricCalculatorModal;
